<template>
  <common-header-cover-page
    v-if="article"
    :header-cover-image="article.headerCoverImage"
    :name="article.title"
  >
    <main
      class="w-full max-w-screen-xl mx-auto px-4 py-8 overflow-x-hidden news-detail"
    >
      <common-breadcrumb class="mb-8 lg:mt-8 lg:mb-16" :crumbs="breadCrumbs" />
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
        <div class="lg:col-span-8">
          <ul
            v-if="article.category !== 'None'"
            class="flex gap-2 mb-2 text-sm text-rhino list-none p-0 items-center"
          >
            <li class="font-bold uppercase">{{ article.type }}</li>
            <li>/</li>
            <li class="font-bold uppercase text-mouse">
              {{ article.category }}
            </li>
          </ul>
          <h1 class="mb-4 leading-tight uppercase">
            {{ article.title }}
          </h1>
          <div
            class="flex border-b border-brand flex-col sm:flex-row sm:gap-4 items-start sm:items-center text-rhino mb-8"
          >
            <div class="flex items-center gap-4 text-sm">
              <div>{{ publishedAt }}</div>
              <div class="flex items-center gap-1">
                <fa-icon :icon="faClock" class="size-4" />
                <span v-if="readingTimeInMinutes > 1">
                  {{
                    t('general/non_special_texts/reading_time', {
                      minutes: readingTimeInMinutes,
                    })
                  }}
                </span>
                <span v-else>
                  {{ t('general/non_special_texts/reading_time_singular') }}
                </span>
              </div>
            </div>
            <social-share :share-text="article.title" />
          </div>

          <!-- main news detail column -->
          <div class="news-detail__main">
            <p class="font-bold my-6 leading-relaxed text-lg">
              {{ article.teaserText }}
            </p>

            <strapi-article-components
              class="news-detail__content mb-8 pb-8 border-b border-brand lg:border-0"
              :class="{
                'lg:border-b': article.footerButtons.length > 0,
              }"
              :article="article"
            />

            <div v-if="article.footerButtons.length > 0" class="mb-16">
              <news-details-footer :buttons="article.footerButtons" />
            </div>
            <social-share :share-text="article.title" />
          </div>
        </div>

        <!-- recent articles list -->
        <div v-if="recentArticles.length > 0" class="lg:col-span-4 lg:mt-32">
          <h3 class="mb-4">{{ t('news/recent_articles') }}:</h3>
          <news-details-recent-list :articles="recentArticles" />
        </div>
      </div>
    </main>
  </common-header-cover-page>
</template>

<script setup lang="ts">
import { faClock } from '@fortawesome/pro-solid-svg-icons';
import { readingTime } from 'reading-time-estimator';
import {
  StrapiArticleType,
  type StrapiProductContent,
} from '~/apollo/types/types';
import type { BreadCrumb } from '~/components/common/types';
import SocialShare from '~/components/news/details/SocialShare.vue';
import { useArticlePublishedDate } from '../features/useArticleFeatures';
import useNewsDetailData from './features/useNewsDetailData';
import { useGoogleBreadcrumb } from '~/helpers/googleBreadcrumb';
import { serializeJsonLd } from '~/helpers/jsonLd';

const props = defineProps<{
  articleType: StrapiArticleType;
}>();

// meta related
const { t, locale } = useI18n();
const route = useRoute();
const config = useRuntimeConfig();

const articleType = toRef(props, 'articleType');
const { newsDetail } = await useNewsDetailData(articleType);

const article = computed(() => newsDetail.value?.article);
const googleNews = computed(() => newsDetail.value?.googleNews);
const recentArticles = computed(() => newsDetail.value?.recentArticles);

const localizedSlugs = computed(() => newsDetail.value.article?.localizedSlugs);
useI18nLocalizedSlugs(localizedSlugs);

const { publishedAt } = useArticlePublishedDate(article, 'long');
/**
 * Calculate reading time. For that, we sum up all text content and calculate
 * the reading time for that. This is the pure reading time, this does not
 * take into account that the user might watch an embedded youtube video or
 * stare on a slideshow image for 10 minutes.
 */
const readingTimeInMinutes = computed(() => {
  if (article.value == null) {
    return 0;
  }

  return article.value.content
    .filter((value) => value.component === 'product.content')
    .reduce(
      (minutes, component: StrapiProductContent) =>
        minutes + Math.ceil(readingTime(component.content).minutes),
      0,
    );
});

// Fill breadcrumbs with the news title
const breadCrumbs = computed(() => {
  const ret = [
    { title: t('general/menu/item/news') as string, link: 'news' },
  ] as Array<BreadCrumb>;

  if (article.value) {
    ret.push({
      title: article.value.title,
      link: `/${article.value.type.toLowerCase()}/${article.value.slug}`,
    });
  }

  return ret;
});

const googleBreadcrumb = computed(() =>
  useGoogleBreadcrumb(
    config.public.websiteUrl,
    locale.value,
    t('app/home'),
    breadCrumbs.value,
    route.path,
  ),
);

useHead({
  title: () => article.value.metaTitle || article.value.title,
  script: () => {
    const scripts = [];
    if (googleNews.value) {
      scripts.push({
        type: 'application/ld+json',
        innerHTML: googleNews.value,
      });
    }
    if (googleBreadcrumb.value) {
      scripts.push({
        type: 'application/ld+json',
        innerHTML: serializeJsonLd(googleBreadcrumb.value),
      });
    }
    return scripts;
  },
});

useSeoMeta({
  title: () => article.value?.metaTitle,
  description: () => article.value?.metaDescription,
  ogTitle: () => article.value?.metaTitle,
  ogDescription: () => article.value?.metaDescription,
  ogImage: () => article.value?.headerCoverImage?.url,
  ogUrl: () => `${config.public.websiteUrl}${route.fullPath}`,
  ogImageAlt: () => article.value?.metaTitle,
  twitterTitle: () => article.value?.metaTitle,
  twitterDescription: () => article.value?.metaDescription,
  twitterImage: () => article.value?.headerCoverImage?.url,
});
</script>

<style>
.news-detail__main .media-gallery__title {
  display: none;
}

.news-detail__main .strapi-product-you-tube__title {
  display: none;
}

.news-detail__main .strapi-product-content hr {
  display: none;
}

.news-detail__main .strapi-components > div {
  margin-bottom: 2rem;
}

.news-detail__main .strapi-components > div:last-child {
  margin-bottom: 0;
}

.news-detail__main a[class*='inline-block'] {
  margin-right: 1rem;
}
</style>
